export type VoicePreset = 'normal' | 'robot' | 'deep' | 'helium' | 'megaphone' | 'alien' | 'echo';

export interface VoicePresetConfig {
    id: VoicePreset;
    label: string;
    icon: string;
    description: string;
}

export const VOICE_PRESETS: VoicePresetConfig[] = [
    { id: 'normal', label: 'Normal', icon: '🎙️', description: 'Original un-processed mic voice' },
    { id: 'robot', label: 'Robot', icon: '🤖', description: 'Ring modulated robotic voice' },
    { id: 'deep', label: 'Deep Monster', icon: '👹', description: 'Pitch shifted deep monster voice' },
    { id: 'helium', label: 'Helium', icon: '🎈', description: 'High pitched cartoon voice' },
    { id: 'megaphone', label: 'Megaphone', icon: '📢', description: 'Distorted radio megaphone' },
    { id: 'alien', label: 'Alien FX', icon: '👽', description: 'Tremolo modulated alien voice' },
    { id: 'echo', label: 'Echo Chamber', icon: '🔊', description: 'Reverberating multi-delay voice' },
];

export function createVoiceFXPipeline(inputStream: MediaStream, preset: VoicePreset): { outputStream: MediaStream; audioContext: AudioContext; cleanup: () => void } {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    const ctx = new AudioCtx();
    const source = ctx.createMediaStreamSource(inputStream);
    const destination = ctx.createMediaStreamDestination();

    if (preset === 'normal') {
        source.connect(destination);
        return {
            outputStream: destination.stream,
            audioContext: ctx,
            cleanup: () => {
                try { ctx.close(); } catch (e) {}
            }
        };
    }

    if (preset === 'robot') {
        // Ring modulation for metallic robot voice
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(50, ctx.currentTime);

        const carrierGain = ctx.createGain();
        carrierGain.gain.setValueAtTime(0.5, ctx.currentTime);

        osc.connect(gain.gain);
        source.connect(gain);
        gain.connect(destination);
        osc.start();

        return {
            outputStream: destination.stream,
            audioContext: ctx,
            cleanup: () => {
                try { osc.stop(); ctx.close(); } catch (e) {}
            }
        };
    }

    if (preset === 'deep') {
        // Deep Pitch Shift simulation with BiquadFilter lowpass + delay modulation
        const filter = ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(450, ctx.currentTime);

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(1.8, ctx.currentTime);

        source.connect(filter);
        filter.connect(gain);
        gain.connect(destination);

        return {
            outputStream: destination.stream,
            audioContext: ctx,
            cleanup: () => {
                try { ctx.close(); } catch (e) {}
            }
        };
    }

    if (preset === 'helium') {
        // High pitch cartoon simulation via highpass + peaking boost
        const filter = ctx.createBiquadFilter();
        filter.type = 'highpass';
        filter.frequency.setValueAtTime(800, ctx.currentTime);

        const peak = ctx.createBiquadFilter();
        peak.type = 'peaking';
        peak.frequency.setValueAtTime(2200, ctx.currentTime);
        peak.Q.setValueAtTime(3, ctx.currentTime);
        peak.gain.setValueAtTime(12, ctx.currentTime);

        source.connect(filter);
        filter.connect(peak);
        peak.connect(destination);

        return {
            outputStream: destination.stream,
            audioContext: ctx,
            cleanup: () => {
                try { ctx.close(); } catch (e) {}
            }
        };
    }

    if (preset === 'megaphone') {
        // Megaphone: Bandpass + Waveshaper Distortion
        const filter = ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.setValueAtTime(1600, ctx.currentTime);
        filter.Q.setValueAtTime(2.5, ctx.currentTime);

        const distortion = ctx.createWaveShaper();
        const curve = new Float32Array(256);
        for (let i = 0; i < 256; ++i) {
            const x = (i * 2) / 256 - 1;
            curve[i] = ((3 + 20) * x * 20 * (Math.PI / 180)) / (Math.PI + 20 * Math.abs(x));
        }
        distortion.curve = curve;

        source.connect(filter);
        filter.connect(distortion);
        distortion.connect(destination);

        return {
            outputStream: destination.stream,
            audioContext: ctx,
            cleanup: () => {
                try { ctx.close(); } catch (e) {}
            }
        };
    }

    if (preset === 'alien') {
        // Tremolo / Alien modulation
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.setValueAtTime(14, ctx.currentTime); // Fast modulation

        osc.connect(gain.gain);
        source.connect(gain);
        gain.connect(destination);
        osc.start();

        return {
            outputStream: destination.stream,
            audioContext: ctx,
            cleanup: () => {
                try { osc.stop(); ctx.close(); } catch (e) {}
            }
        };
    }

    if (preset === 'echo') {
        // Echo delay pipeline
        const delay = ctx.createDelay();
        delay.delayTime.setValueAtTime(0.25, ctx.currentTime);

        const feedback = ctx.createGain();
        feedback.gain.setValueAtTime(0.4, ctx.currentTime);

        source.connect(destination);
        source.connect(delay);
        delay.connect(feedback);
        feedback.connect(delay);
        delay.connect(destination);

        return {
            outputStream: destination.stream,
            audioContext: ctx,
            cleanup: () => {
                try { ctx.close(); } catch (e) {}
            }
        };
    }

    source.connect(destination);
    return {
        outputStream: destination.stream,
        audioContext: ctx,
        cleanup: () => {
            try { ctx.close(); } catch (e) {}
        }
    };
}

export async function convertVoiceNoteElevenLabs(audioBlob: Blob, voiceId: string = '21m00Tcm4TlvDq8ikWAM', apiKey?: string): Promise<Blob> {
    const keyToUse = apiKey || process.env.REACT_APP_ELEVENLABS_API_KEY || 'sk_mock_key';

    const formData = new FormData();
    formData.append('audio', audioBlob, 'voicenote.mp3');
    formData.append('model_id', 'eleven_english_sts_v2');

    const response = await fetch(`https://api.elevenlabs.io/v1/speech-to-speech/${voiceId}`, {
        method: 'POST',
        headers: {
            'xi-api-key': keyToUse,
        },
        body: formData,
    });

    if (!response.ok) {
        throw new Error(`ElevenLabs transformation failed with status ${response.status}`);
    }

    return await response.blob();
}
